import { CustomerProfile, OwnerProfile, UserSession } from '../types';
import { syncCustomersToSupabase, syncOwnersToSupabase } from './supabaseSync';
import { logAuditEvent } from './auditStore';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

const OWNERS_KEY = 'halisaha_owner_profiles';
const CUSTOMERS_KEY = 'halisaha_customer_profiles';
const SESSION_KEY = 'halisaha_user_session';
const SESSION_EVENT = 'halisaha:session-changed';
let sessionCacheRaw: string | null = null;
let sessionCacheValue: UserSession | null = null;

const ADMIN_USERNAME = 'ADMİN';
const ADMIN_PASSWORD = 'Admin53';

function safeRead<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function safeWrite<T>(key: string, value: T) {
  localStorage.setItem(key, JSON.stringify(value));
}

function readSession(): UserSession | null {
  try {
    const tabRaw = sessionStorage.getItem(SESSION_KEY);
    if (tabRaw) {
      // Keep snapshot reference stable for useSyncExternalStore consumers.
      if (tabRaw === sessionCacheRaw) return sessionCacheValue;
      const parsed = JSON.parse(tabRaw) as UserSession;
      sessionCacheRaw = tabRaw;
      sessionCacheValue = parsed;
      return parsed;
    }
    sessionCacheRaw = null;
    sessionCacheValue = null;
    localStorage.removeItem(SESSION_KEY);
    return null;
  } catch {
    sessionCacheRaw = null;
    sessionCacheValue = null;
    return null;
  }
}

function writeSession(session: UserSession) {
  const raw = JSON.stringify(session);
  sessionStorage.setItem(SESSION_KEY, raw);
  sessionCacheRaw = raw;
  sessionCacheValue = session;
  window.dispatchEvent(new CustomEvent(SESSION_EVENT));
}

function normalizeUsername(value?: string): string {
  return String(value || '').trim().toLowerCase();
}

function normalizePhone(value?: string): string {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.startsWith('90') && digits.length === 12) return `0${digits.slice(2)}`;
  if (digits.length === 10) return `0${digits}`;
  return digits;
}

function migrateOwner(owner: OwnerProfile): OwnerProfile {
  if (owner.username?.trim()) return { ...owner, suspended: Boolean(owner.suspended) };
  const fallback = owner.email?.split('@')[0] || normalizePhone(owner.phone) || `owner-${owner.id.slice(-4)}`;
  return { ...owner, username: fallback, suspended: Boolean(owner.suspended) };
}

function migrateCustomer(customer: CustomerProfile): CustomerProfile {
  if (customer.username?.trim()) return { ...customer, suspended: Boolean(customer.suspended) };
  const fallback = customer.email?.split('@')[0] || normalizePhone(customer.phone) || `user-${customer.id.slice(-4)}`;
  return { ...customer, username: fallback, suspended: Boolean(customer.suspended) };
}

export function getOwnerProfiles(): OwnerProfile[] {
  const owners = safeRead<OwnerProfile[]>(OWNERS_KEY, []).map(migrateOwner);
  safeWrite(OWNERS_KEY, owners);
  return owners;
}

export function getCustomerProfiles(): CustomerProfile[] {
  const customers = safeRead<CustomerProfile[]>(CUSTOMERS_KEY, []).map(migrateCustomer);
  safeWrite(CUSTOMERS_KEY, customers);
  return customers;
}

export function getPendingOwnerProfiles(): OwnerProfile[] {
  return getOwnerProfiles().filter((owner) => owner.status === 'pending');
}

export function getOwnerProfileById(ownerId: string): OwnerProfile | undefined {
  return getOwnerProfiles().find((owner) => owner.id === ownerId);
}

export function getCustomerById(userId: string): CustomerProfile | undefined {
  return getCustomerProfiles().find((customer) => customer.id === userId);
}

export function registerOwnerProfile(input: {
  fullName: string;
  username?: string;
  phone: string;
  password: string;
  district: string;
}): { ok: true; owner: OwnerProfile } | { ok: false; error: string } {
  const owners = getOwnerProfiles();
  const fallbackUsername = normalizePhone(input.phone) || `owner-${Date.now()}`;
  const resolvedUsername = String(input.username || fallbackUsername).trim();
  const normalizedUsername = normalizeUsername(resolvedUsername);
  const normalizedPhone = normalizePhone(input.phone);

  const usernameExists = owners.some((owner) => normalizeUsername(owner.username) === normalizedUsername);
  if (usernameExists) {
    return { ok: false, error: 'Bu kullanıcı adı zaten alınmış.' };
  }

  const phoneExists = owners.some((owner) => normalizePhone(owner.phone) === normalizedPhone);
  if (phoneExists) {
    return { ok: false, error: 'Bu telefon numarası ile daha önce profil açılmış.' };
  }

  const owner: OwnerProfile = {
    id: `owner-${Date.now()}`,
    fullName: input.fullName.trim(),
    username: resolvedUsername,
    phone: input.phone.trim(),
    password: input.password,
    district: input.district,
    status: 'pending',
    suspended: false,
    createdAt: new Date().toISOString(),
  };

  safeWrite(OWNERS_KEY, [owner, ...owners]);
  syncOwnersToSupabase();
  void ensureOwnerSupabaseSession(owner, input.password);
  logAuditEvent({
    session: getSession(),
    action: 'owner.registered',
    targetType: 'owner',
    targetId: owner.id,
    meta: { username: owner.username, district: owner.district },
  });
  return { ok: true, owner };
}

export function approveOwnerProfile(ownerId: string): boolean {
  const owners = getOwnerProfiles();
  const index = owners.findIndex((owner) => owner.id === ownerId);
  if (index < 0) return false;
  owners[index] = { ...owners[index], status: 'approved', suspended: false };
  safeWrite(OWNERS_KEY, owners);
  syncOwnersToSupabase();
  logAuditEvent({
    session: getSession(),
    action: 'owner.approved',
    targetType: 'owner',
    targetId: ownerId,
  });
  return true;
}

export function registerCustomerProfile(input: {
  fullName: string;
  username: string;
  phone: string;
  password: string;
}): { ok: true; user: CustomerProfile } | { ok: false; error: string } {
  const customers = getCustomerProfiles();
  const normalizedUsername = normalizeUsername(input.username);
  const normalizedPhone = normalizePhone(input.phone);

  const usernameExists = customers.some((customer) => normalizeUsername(customer.username) === normalizedUsername);
  if (usernameExists) {
    return { ok: false, error: 'Bu kullanıcı adı zaten alınmış.' };
  }

  const phoneExists = customers.some((customer) => normalizePhone(customer.phone) === normalizedPhone);
  if (phoneExists) {
    return { ok: false, error: 'Bu telefon numarası ile kullanıcı hesabı zaten var.' };
  }

  const user: CustomerProfile = {
    id: `user-${Date.now()}`,
    fullName: input.fullName.trim(),
    username: input.username.trim(),
    phone: input.phone.trim(),
    password: input.password,
    suspended: false,
    createdAt: new Date().toISOString(),
  };

  safeWrite(CUSTOMERS_KEY, [user, ...customers]);
  syncCustomersToSupabase();
  logAuditEvent({
    session: getSession(),
    action: 'customer.registered',
    targetType: 'customer',
    targetId: user.id,
    meta: { username: user.username },
  });
  return { ok: true, user };
}

export function loginOrRegisterCustomerWithGoogle(input: {
  googleId: string;
  email: string;
  fullName: string;
}): { ok: true; user: CustomerProfile } | { ok: false; error: string } {
  const customers = getCustomerProfiles();
  const normalizedEmail = input.email.trim().toLowerCase();
  const existingIndex = customers.findIndex(
    (item) => item.googleId === input.googleId || item.email?.trim().toLowerCase() === normalizedEmail
  );

  if (existingIndex >= 0) {
    const merged: CustomerProfile = {
      ...customers[existingIndex],
      fullName: input.fullName.trim() || customers[existingIndex].fullName,
      email: input.email.trim(),
      googleId: input.googleId,
    };
    if (merged.suspended) {
      return { ok: false, error: 'Hesabınız askıya alınmış. Lütfen destek ile iletişime geçin.' };
    }
    customers[existingIndex] = merged;
    safeWrite(CUSTOMERS_KEY, customers);
    syncCustomersToSupabase();
    writeSession({ role: 'customer', userId: merged.id });
    void ensureGoogleSupabaseUserMetadata(merged);
    logAuditEvent({
      session: { role: 'customer', userId: merged.id },
      action: 'customer.login.google',
      targetType: 'customer',
      targetId: merged.id,
    });
    return { ok: true, user: merged };
  }

  const user: CustomerProfile = {
    id: `user-${Date.now()}`,
    fullName: input.fullName.trim() || 'Google Kullanıcısı',
    username: input.email.split('@')[0] || `google-${Date.now()}`,
    email: input.email.trim(),
    phone: '',
    password: '',
    googleId: input.googleId,
    suspended: false,
    createdAt: new Date().toISOString(),
  };
  safeWrite(CUSTOMERS_KEY, [user, ...customers]);
  syncCustomersToSupabase();
  writeSession({ role: 'customer', userId: user.id });
  void ensureGoogleSupabaseUserMetadata(user);
  logAuditEvent({
    session: { role: 'customer', userId: user.id },
    action: 'customer.registered.google',
    targetType: 'customer',
    targetId: user.id,
  });
  return { ok: true, user };
}

export function rejectOwnerProfile(ownerId: string): boolean {
  const owners = getOwnerProfiles();
  const index = owners.findIndex((owner) => owner.id === ownerId);
  if (index < 0) return false;
  owners[index] = { ...owners[index], status: 'rejected' };
  safeWrite(OWNERS_KEY, owners);
  syncOwnersToSupabase();
  logAuditEvent({
    session: getSession(),
    action: 'owner.rejected',
    targetType: 'owner',
    targetId: ownerId,
  });
  return true;
}

export function setOwnerSuspended(ownerId: string, suspended: boolean): boolean {
  const owners = getOwnerProfiles();
  const index = owners.findIndex((owner) => owner.id === ownerId);
  if (index < 0) return false;
  owners[index] = { ...owners[index], suspended };
  safeWrite(OWNERS_KEY, owners);
  syncOwnersToSupabase();
  logAuditEvent({
    session: getSession(),
    action: suspended ? 'owner.suspended' : 'owner.unsuspended',
    targetType: 'owner',
    targetId: ownerId,
  });
  return true;
}

export function setCustomerSuspended(userId: string, suspended: boolean): boolean {
  const customers = getCustomerProfiles();
  const index = customers.findIndex((item) => item.id === userId);
  if (index < 0) return false;
  customers[index] = { ...customers[index], suspended };
  safeWrite(CUSTOMERS_KEY, customers);
  syncCustomersToSupabase();
  logAuditEvent({
    session: getSession(),
    action: suspended ? 'customer.suspended' : 'customer.unsuspended',
    targetType: 'customer',
    targetId: userId,
  });
  return true;
}

export function deleteOwnerProfile(ownerId: string): boolean {
  const owners = getOwnerProfiles();
  const filtered = owners.filter((item) => item.id !== ownerId);
  if (filtered.length === owners.length) return false;
  safeWrite(OWNERS_KEY, filtered);
  syncOwnersToSupabase();
  logAuditEvent({
    session: getSession(),
    action: 'owner.deleted',
    targetType: 'owner',
    targetId: ownerId,
  });
  return true;
}

export function deleteCustomerProfile(userId: string): boolean {
  const customers = getCustomerProfiles();
  const filtered = customers.filter((item) => item.id !== userId);
  if (filtered.length === customers.length) return false;
  safeWrite(CUSTOMERS_KEY, filtered);
  syncCustomersToSupabase();
  logAuditEvent({
    session: getSession(),
    action: 'customer.deleted',
    targetType: 'customer',
    targetId: userId,
  });
  return true;
}

export function loginAdmin(username: string, password: string): boolean {
  if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
    writeSession({ role: 'admin' });
    logAuditEvent({
      session: { role: 'admin' },
      action: 'admin.login',
      targetType: 'system',
    });
    return true;
  }
  return false;
}

export function loginOwner(identifier: string, password: string): { ok: true; owner: OwnerProfile } | { ok: false; error: string } {
  const normalizedIdentifier = normalizeUsername(identifier);
  const normalizedIdentifierPhone = normalizePhone(identifier);

  const owner = getOwnerProfiles().find((item) => {
    const usernameMatch = normalizeUsername(item.username) === normalizedIdentifier;
    const phoneMatch = Boolean(normalizedIdentifierPhone) && normalizePhone(item.phone) === normalizedIdentifierPhone;
    return (usernameMatch || phoneMatch) && item.password === password;
  });

  if (!owner) return { ok: false, error: 'Giriş bilgileri hatalı.' };
  if (owner.status === 'rejected') {
    return { ok: false, error: 'Profiliniz admin tarafından reddedildi.' };
  }
  if (owner.suspended) {
    return { ok: false, error: 'Profiliniz askıya alınmış.' };
  }
  writeSession({ role: 'owner', ownerId: owner.id });
  void ensureOwnerSupabaseSession(owner, password);
  void upsertSupabaseUserMetadata({ role: 'owner', ownerId: owner.id });
  logAuditEvent({
    session: { role: 'owner', ownerId: owner.id },
    action: 'owner.login',
    targetType: 'owner',
    targetId: owner.id,
  });
  return { ok: true, owner };
}

export function loginCustomer(identifier: string, password: string): { ok: true; user: CustomerProfile } | { ok: false; error: string } {
  const normalizedIdentifier = normalizeUsername(identifier);
  const normalizedIdentifierPhone = normalizePhone(identifier);

  const user = getCustomerProfiles().find((item) => {
    const usernameMatch = normalizeUsername(item.username) === normalizedIdentifier;
    const phoneMatch = Boolean(normalizedIdentifierPhone) && normalizePhone(item.phone) === normalizedIdentifierPhone;
    return (usernameMatch || phoneMatch) && Boolean(item.password) && item.password === password;
  });

  if (!user) return { ok: false, error: 'Giriş bilgileri hatalı.' };
  if (user.suspended) return { ok: false, error: 'Hesabınız askıya alınmış.' };
  writeSession({ role: 'customer', userId: user.id });
  void ensureCustomerSupabaseSession(user, password);
  void upsertSupabaseUserMetadata({ role: 'customer', userId: user.id });
  logAuditEvent({
    session: { role: 'customer', userId: user.id },
    action: 'customer.login',
    targetType: 'customer',
    targetId: user.id,
  });
  return { ok: true, user };
}

export function setOwnerSession(ownerId: string) {
  writeSession({ role: 'owner', ownerId });
  void upsertSupabaseUserMetadata({ role: 'owner', ownerId });
}

export function setCustomerSession(userId: string) {
  writeSession({ role: 'customer', userId });
  void upsertSupabaseUserMetadata({ role: 'customer', userId });
}

export function logoutUser() {
  const session = getSession();
  if (isSupabaseConfigured && supabase) {
    void supabase.auth.signOut();
  }
  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(SESSION_KEY);
  sessionCacheRaw = null;
  sessionCacheValue = null;
  window.dispatchEvent(new CustomEvent(SESSION_EVENT));
  logAuditEvent({
    session,
    action: 'auth.logout',
    targetType: 'system',
  });
}

export function getSession(): UserSession | null {
  return readSession();
}

export function subscribeSession(listener: () => void): () => void {
  const handler = () => listener();
  window.addEventListener(SESSION_EVENT, handler);
  return () => {
    window.removeEventListener(SESSION_EVENT, handler);
  };
}

function buildAuthEmailFromPhone(phone: string): string {
  const normalized = normalizePhone(phone) || String(phone).replace(/\s+/g, '');
  return `${normalized}@auth.macvakti.local`;
}

async function upsertSupabaseUserMetadata(session: UserSession): Promise<void> {
  if (!isSupabaseConfigured || !supabase) return;
  const appUserId = session.role === 'admin' ? 'admin' : session.role === 'owner' ? session.ownerId : session.userId;
  const appRole = session.role;
  try {
    await supabase.auth.updateUser({
      data: {
        app_role: appRole,
        app_user_id: appUserId,
      },
    });
    await supabase.auth.refreshSession();
  } catch (error) {
    console.warn('[auth] failed to update supabase user metadata', error);
  }
}

async function ensureOwnerSupabaseSession(owner: OwnerProfile, password: string): Promise<void> {
  if (!isSupabaseConfigured || !supabase) return;
  const email = buildAuthEmailFromPhone(owner.phone);
  const signIn = await supabase.auth.signInWithPassword({ email, password });
  if (!signIn.error) return;
  await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        app_role: 'owner',
        app_user_id: owner.id,
      },
    },
  });
}

async function ensureCustomerSupabaseSession(user: CustomerProfile, password: string): Promise<void> {
  if (!isSupabaseConfigured || !supabase || !user.phone) return;
  const email = buildAuthEmailFromPhone(user.phone);
  const signIn = await supabase.auth.signInWithPassword({ email, password });
  if (!signIn.error) return;
  await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        app_role: 'customer',
        app_user_id: user.id,
      },
    },
  });
}

async function ensureGoogleSupabaseUserMetadata(user: CustomerProfile): Promise<void> {
  if (!isSupabaseConfigured || !supabase) return;
  await upsertSupabaseUserMetadata({ role: 'customer', userId: user.id });
}

export async function beginGoogleLoginWithSupabase(): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!isSupabaseConfigured || !supabase) {
    return { ok: false, error: 'Supabase ayarı eksik.' };
  }

  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/giris`,
      queryParams: {
        access_type: 'offline',
        prompt: 'select_account',
      },
    },
  });

  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export async function syncSessionFromSupabase(): Promise<UserSession | null> {
  if (!isSupabaseConfigured || !supabase) return getSession();
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.user) return getSession();

  const user = data.session.user;
  const metaRole = String(user.user_metadata?.app_role || '');
  const metaUserId = String(user.user_metadata?.app_user_id || '');
  if (metaRole === 'customer' && metaUserId) {
    const session: UserSession = { role: 'customer', userId: metaUserId };
    writeSession(session);
    return session;
  }
  if (metaRole === 'owner' && metaUserId) {
    const session: UserSession = { role: 'owner', ownerId: metaUserId };
    writeSession(session);
    return session;
  }

  const email = String(user.email || '').trim().toLowerCase();
  if (!email) return getSession();
  const customers = getCustomerProfiles();
  const existingIndex = customers.findIndex((item) => item.email?.trim().toLowerCase() === email || item.googleId === user.id);
  let profile: CustomerProfile;
  if (existingIndex >= 0) {
    profile = {
      ...customers[existingIndex],
      fullName: String(user.user_metadata?.full_name || user.user_metadata?.name || customers[existingIndex].fullName || email),
      email: user.email || customers[existingIndex].email,
      googleId: user.id,
    };
    customers[existingIndex] = profile;
  } else {
    profile = {
      id: `user-${Date.now()}`,
      fullName: String(user.user_metadata?.full_name || user.user_metadata?.name || email),
      username: email.split('@')[0] || `google-${Date.now()}`,
      email,
      phone: '',
      password: '',
      googleId: user.id,
      suspended: false,
      createdAt: new Date().toISOString(),
    };
    customers.unshift(profile);
  }

  if (profile.suspended) return null;
  safeWrite(CUSTOMERS_KEY, customers);
  syncCustomersToSupabase();
  const session: UserSession = { role: 'customer', userId: profile.id };
  writeSession(session);
  await ensureGoogleSupabaseUserMetadata(profile);
  return session;
}
